import React, {useEffect} from "react";
import styled, {css} from "styled-components";
import {Box} from "@mui/material";
import {Exist, HOCAccordion} from "components";
import {PureButton} from "components/Button";
import {useTranslation} from "react-i18next";
import {useDispatch, useSelector} from "react-redux";
import {isMenuOpen} from "modules/selectors";
import {toggleMenu} from "modules/actions";
import {LANGUAGE_PATH, ProjectName} from "modules";
import {ISubNavigationItem} from "modules/types";
import {NavLink, useLocation} from "react-router-dom";
import MenuIcon from "@mui/icons-material/Menu";
import chevron from "assets/images/icons/chevron_down_black.svg";
import {get} from "lodash";
import {MobileLanguageSelector} from "./MobileLanguageSelector";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import {IS_PRE_DRAW} from "apps/DRAW_PREDICTOR/modules/constants";

const MenuToggle = styled(PureButton)`
	margin-inline-end: 10px;

	img {
		vertical-align: middle;
	}
`;

const Menu = styled.div`
	list-style: none;
	padding: 30px 0 12px;
	padding-inline-start: 44px;
	position: fixed;
	top: 60px;
	height: 100%;
	width: 100%;
	left: 0;
	background-color: ${({theme}) => theme.global.header_background};
	transform: translateX(-100%);
	transition: 0.25s;
	border: 1px solid #e8e6e2;
	.top-active {
		color: #550065;
		text-transform: uppercase;
		font-size: 14px;
		line-height: 160%;
		font-family: "Qatar2022", sans-serif;
		font-weight: 700;
	}

	.final {
		margin-inline-start: 20px;
	}

	&.open {
		transform: unset;
	}
`;

const styles = css`
	padding: 14px 0;
	display: block;
	color: #000;
	font-style: normal;
	font-size: 14px;
	line-height: 160%;
	font-family: "Qatar2022", sans-serif;
	font-weight: 700;

	&.active,
	&:focus {
		color: ${({theme}) => theme.global.header_hover_color};
	}
`;

const LinkWrapper = styled.div`
	width: 100%;
	&.spaced {
		padding-top: 90px;
	}
	color: #000;
	a {
		${styles}
	}
`;

const TutorialLink = styled(NavLink)`
	${styles}
	&.active.tutorial {
		color: #000;
	}
`;

const ExternalLink = styled.a`
	&.external {
		display: flex;
		justify-content: space-between;
		align-items: center;
		padding-inline-end: 22px;
		margin-inline-start: 20px;
		color: ${({theme}) => theme.global.header_hover_color};
		span {
			margin-inline-end: auto;
			color: ${({theme}) => theme.global.header_hover_color};
		}
	}

	&.with-icon {
		span,
		svg {
			color: ${({theme}) => theme.global.header_hover_color};
			position: relative;
			left: -12px;
		}
	}
`;

const DRAW_PREDICTOR_SUB_NAVIGATION: ISubNavigationItem[] = IS_PRE_DRAW
	? [
			{
				name: "draw.header.game_name",
				to: "/",
				href: "/draw-predictor",
				projectName: ProjectName.Predictor,
				customClass: "top-active",
				subItems: [],
			},
	  ]
	: [
			{
				name: "draw.header.game_name",
				to: "/",
				href: "/draw-predictor",
				projectName: ProjectName.Predictor,
				customClass: "top-active",
				subItems: [
					{
						name: "draw.header.my_draw",
						to: "/",
						href: "/draw-predictor/",
						subItems: [],
						projectName: ProjectName.Predictor,
						customClass: "final",
					},
					{
						name: "draw.header.prizes",
						to: "/help/prizes",
						href: "/draw-predictor/help/prizes",
						subItems: [],
						projectName: ProjectName.Predictor,
						customClass: "final",
					},
					{
						name: "draw.header.help",
						to: "/help",
						href: "/draw-predictor/help",
						projectName: ProjectName.Predictor,
						customClass: "final",
						subItems: [
							{
								name: "draw.header.how_to",
								to: "/help/guidelines",
								href: "/draw-predictor/help/guidelines",
								subItems: [],
								projectName: ProjectName.Predictor,
							},
							{
								name: "draw.header.faqs",
								to: "/help/faqs",
								href: "/draw-predictor/help/faqs",
								subItems: [],
								projectName: ProjectName.Predictor,
							},
						],
					},
				],
			},
	  ];

interface IMenuProps {
	showTutorial: () => void;
}

export const HamburgerMenu: React.FC<IMenuProps> = ({showTutorial}) => {
	const dispatch = useDispatch();
	const location = useLocation();
	const {t: global} = useTranslation();
	const {t} = useTranslation(LANGUAGE_PATH);
	const isOpen = useSelector(isMenuOpen);
	const openClass = isOpen ? "open" : "";

	const toggleMobileMenu = () => {
		dispatch(toggleMenu(!isOpen));
	};

	useEffect(() => {
		dispatch(toggleMenu(false));
	}, [dispatch, location]);

	const items = get(DRAW_PREDICTOR_SUB_NAVIGATION, "[0].subItems", []) as ISubNavigationItem[];

	return (
		<Box
			sx={{
				position: "relative",
			}}>
			<MenuToggle onClick={toggleMobileMenu}>
				<MenuIcon />
			</MenuToggle>
			<Menu className={openClass}>
				<div className="shadow" />
				<HOCAccordion
					icon={chevron}
					open={true}
					item={<MenuItemLink item={DRAW_PREDICTOR_SUB_NAVIGATION[0]} />}>
					{items.map((item) => (
						<LinkWrapper key={item.name}>
							<SubAccordion item={item} />
						</LinkWrapper>
					))}
					<Exist when={!IS_PRE_DRAW}>
						<TutorialLink to="/" className="final tutorial" onClick={showTutorial}>
							{t("draw.header.tutorial")}
						</TutorialLink>
					</Exist>

					<MobileLanguageSelector />
				</HOCAccordion>

				<LinkWrapper className="spaced">
					<ExternalLink className="external" href="https://www.fifa.com/">
						<span>{global("subheader.fifa.fifa_com")}</span>
						<OpenInNewIcon fontSize="small" />
					</ExternalLink>
				</LinkWrapper>
			</Menu>
		</Box>
	);
};

const SubLinkWrapper = styled.div`
	padding-inline-start: 20px;
	color: #000;
	> a {
		font-family: "Qatar2022", sans-serif;
		font-style: normal;
		font-weight: 400;
		font-size: 14px;
		line-height: 160%;
		text-transform: uppercase;
	}
	> a.active {
		color: ${({theme}) => theme.global.header_hover_color};
	}
`;

interface ISubAccordionProps {
	item: ISubNavigationItem;
}

const MenuItemLink: React.FC<ISubAccordionProps> = ({item}) => {
	const {t} = useTranslation(LANGUAGE_PATH);

	return (
		<NavLink end to={item.to} className={item.customClass}>
			{t(item.name)}
		</NavLink>
	);
};

export const SubAccordion: React.FC<ISubAccordionProps> = ({item}) => {
	const isSubItemsVisible = item.subItems?.length !== 0;

	if (isSubItemsVisible) {
		return (
			<HOCAccordion icon={chevron} item={<MenuItemLink item={item} />}>
				{item.subItems?.map((subItem) => (
					<SubLinkWrapper key={subItem.name}>
						<MenuItemLink item={subItem} />
					</SubLinkWrapper>
				))}
			</HOCAccordion>
		);
	}

	return <MenuItemLink item={item} />;
};
